// import { updateMetaTags } from '../../utils/meta-tags-updater';
import { lazy, Suspense } from 'react';
import { Box, Container, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { API } from '../../interceptors/axios'
// import Categories from "../categories/Categories";
import Hero from "../hero/Hero";

import BackToTopButton from "../back-to-top-button/BackToTopButton"
import "./Home.css";
import "../../fonts/dameronbold.ttf";
import { fetchAuth } from '../../utils/auth';

import Spinner from "../spinner/Spinner";

// import { isSocialMediaPreview, getSocialPlatform } from '../../utils/social-media-detector';

const LazySongs = lazy(() => import('../songs/Songs'));
const LazyRightbar = lazy(() => import('../rightbar/Rightbar'));


const SpinnerWrapper = ({ children }) => (
  <Box
    sx={{
      position: 'relative',
      minHeight: '100px',
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      py: 2
    }}
  >
    {children}
  </Box>
);

const ImageCache = {
    store: new Map(),
    blobUrls: new Set(),
    
    async get(blogId) {
      try {
            // Try memory cache first
            if (this.store.has(blogId)) {
                const cached = this.store.get(blogId);
                // Verify blob URL is still valid
                const response = await fetch(cached);
                if (response.ok) return cached;
                
                // If URL is invalid, remove it
                this.store.delete(blogId);
                this.blobUrls.delete(cached);
                sessionStorage.removeItem(`image_${blogId}`);
            }
            
            // Try session storage
            const cacheKey = `image_${blogId}`;
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
                try {
                    const response = await fetch(cached);
                    if (response.ok) {
                        this.store.set(blogId, cached);
                        this.blobUrls.add(cached);
                        return cached;
                    }
                    sessionStorage.removeItem(cacheKey);
                } catch {
                    sessionStorage.removeItem(cacheKey);
                }
            }
            
            return null;
        } catch {
            return null;
        }
    },
    
    set(blogId, blob) {
       try {
        const url = URL.createObjectURL(blob);
        const cacheKey = `image_${blogId}`;
        this.store.set(blogId, url);
        this.blobUrls.add(url);
        sessionStorage.setItem(cacheKey, url);
        return url;
      } catch (error) {
          console.error('Failed to cache image:', error);
          // Direct URL fallback for previews
          // This is a fallback for when the blob URL fails to create
          // ToDo: replace this with a separate image URL
          return `${process.env.REACT_APP_API_URL}/public-preview/${blogId}.jpg?v=${Date.now()}`;
        }
    },
    
    clear() {
        // Revoke all blob URLs
        this.blobUrls.forEach(url => {
            try {
                URL.revokeObjectURL(url);
            } catch (error) {
                console.error('Failed to revoke URL:', error);
            }
        });
        this.blobUrls.clear();
        this.store.clear();
    }
};


const Home = ({sendPage}) => { 
  // const defaultPreviewUrl = `${process.env.REACT_APP_API_URL}/default-preview.jpg?v=${Date.now()}`;

  // Pre-set meta tags for crawlers as early as possible
  // if (typeof window !== 'undefined' && isSocialMediaPreview()) {
  //   console.log("Pre-setting meta tags for crawler");
  //   updateMetaTags({
  //     title: 'Distorise Music Blog',
  //     description: 'Discover and enjoy unique music tracks on Distorise Music Blog',
  //     imageUrl: defaultPreviewUrl,
  //     type: 'website'
  //   });
  // }

  const [blogImageMap, setBlogImageMap] = useState({});
  const [blogData, setBlogData] = useState(null)
  const [imgSrc, setImgSrc] = useState([])
  const [loadingStates, setLoadingStates] = useState({
    popularBlogs: true,
    popularImages: false
  })


  const fetchBlogsData = async () => {    
      try {
          await fetchAuth();
          const token = localStorage.getItem("token");
          if (!token) throw new Error('No token available');

          // Get blog data first
          const blogs_popular_data = await API.get('/blogs-popular/');
          setBlogData(blogs_popular_data);
          setLoadingStates(prev => ({...prev, popularBlogs: false, popularImages: true}));

          // Create image loading queue
          const loadImageQueue = blogs_popular_data.data.map(blog => ({
              id: blog.song,
              loaded: false,
              retries: 0
          }));

          // Create intersection observer
          const imageObserver = new IntersectionObserver(
              (entries) => {
                  entries.forEach(entry => {
                      if (entry.isIntersecting) {
                          const blogId = entry.target.dataset.blogId;
                          loadImage(blogId);
                          imageObserver.unobserve(entry.target);
                      }
                  });
              },
              { rootMargin: '50px' }
          );

          // Load image function with retries and caching
          const loadImage = async (blogId) => {
              const queueItem = loadImageQueue.find(item => item.id === blogId);
              if (!queueItem || queueItem.loaded || queueItem.retries >= 2) return;

              try {
                  // Check cache first
                  const cachedUrl = await ImageCache.get(blogId);
                  if (cachedUrl) {
                      // Update both the image array and the mapping
                      setImgSrc(prev => [...prev, cachedUrl]);
                      // Important: Update the mapping
                      setBlogImageMap(prev => ({
                          ...prev,
                          [blogId]: cachedUrl // Store image URL by blog ID
                      }));
                      queueItem.loaded = true;
                      return;
                  }

                  // Load from API if not cached
                  const res = await API.get(`/image/${blogId}`, {
                      responseType: 'blob',
                      headers: {
                          'Content-Type': 'image/png',
                          'Cache-Control': 'max-age=3600'
                      }
                  });

                  const blob = new Blob([res.data], { type: 'image/png' });
                  const url = ImageCache.set(blogId, blob);
                  
                  // Update both the image array and the mapping
                  setImgSrc(prev => [...prev, url]);
                  // Important: Update the mapping
                  setBlogImageMap(prev => ({
                      ...prev,
                      [blogId]: url // Store image URL by blog ID
                  }));
                  queueItem.loaded = true;
              } catch (error) {
                  console.error(`Failed to load image ${blogId}:`, error);
                  queueItem.retries++;
                  
                  if (queueItem.retries < 2) {
                      await fetchAuth();
                      setTimeout(() => loadImage(blogId), 1000);
                  }
              }
          };

          // Set up observers for visible elements
          blogs_popular_data.data.forEach(blog => {
              const element = document.querySelector(`[data-blog-id="${blog.song}"]`);
              if (element) imageObserver.observe(element);
          });

          // First load the initial images (first 3) immediately
          // console.log('Loading first 3 visible images...');
          const visibleImages = loadImageQueue.slice(0, 3);
          await Promise.all(visibleImages.map(item => loadImage(item.id)));

          // Now, load the remaining images in background instead of using observer
          // console.log('Loading remaining images in background...');
          const remainingImages = loadImageQueue.slice(3);

          // Optimized loading with proper timeouts and error handling
          const loadRemainingImagesOptimized = async () => {
              // Load in larger parallel batches
              const BATCH_SIZE = 4; // Load 4 at a time instead of 2
              const MAX_TIME_PER_IMAGE = 5000; // 5 seconds max per image
              
              // Create a function to load a single image with timeout
              const loadImageWithTimeout = async (item) => {
                  return new Promise(resolve => {
                      // Set timeout to avoid hanging requests
                      const timeout = setTimeout(() => {
                          console.warn(`Image ${item.id} load timed out after ${MAX_TIME_PER_IMAGE}ms`);
                          // Add a fallback image URL to the map
                          setBlogImageMap(prev => ({
                              ...prev,
                              [item.id]: `${process.env.REACT_APP_API_URL}/public-image/${item.id}?t=${Date.now()}`
                          }));
                          resolve(false); // Indicate failure
                      }, MAX_TIME_PER_IMAGE);
                      
                      // Start loading the image
                      loadImage(item.id)
                          .then(() => {
                              clearTimeout(timeout);
                              resolve(true); // Indicate success
                          })
                          .catch(error => {
                              console.error(`Error loading image ${item.id}:`, error);
                              clearTimeout(timeout);
                              // Add a fallback image URL
                              setBlogImageMap(prev => ({
                                  ...prev,
                                  [item.id]: `${process.env.REACT_APP_API_URL}/public-image/${item.id}?t=${Date.now()}`
                              }));
                              resolve(false); // Indicate failure
                          });
                  });
              };
              
              // Process all remaining images in parallel batches
              for (let i = 0; i < remainingImages.length; i += BATCH_SIZE) {
                  const batch = remainingImages.slice(i, Math.min(i + BATCH_SIZE, remainingImages.length));
                  
                  // console.log(`Loading batch ${Math.floor(i/BATCH_SIZE) + 1} of ${Math.ceil(remainingImages.length/BATCH_SIZE)}`);
                  
                  // Load this batch in parallel
                  const results = await Promise.all(
                      batch.map(item => loadImageWithTimeout(item))
                  );
                  
                  // Log batch results
                  const successes = results.filter(Boolean).length;
                  // console.log(`Batch complete: ${successes}/${batch.length} images loaded`);
                  
                  // No delay between batches - immediately start the next one
              }
              
              // console.log('All remaining images processed');
          };

          // Start the optimized loading immediately (no 500ms delay)
          loadRemainingImagesOptimized();

          setLoadingStates(prev => ({...prev, popularImages: false}));

      } catch (error) {
          console.error("Error fetching data:", error);
          setLoadingStates(prev => ({
              ...prev, 
              popularBlogs: false, 
              popularImages: false
          }));
      }
  };


  useEffect(() => {    
      const initializeData = async () => {
          try {
              
            // if (isSocialMediaPreview()) {
            //     const platform = getSocialPlatform();
            //     console.log(`Social media crawler detected on home page: ${platform || 'unknown'}`);
                
            //     updateMetaTags({
            //         title: 'Distorise Music Blog',
            //         description: 'Discover and enjoy unique music tracks on Distorise Music Blog',
            //         imageUrl: defaultPreviewUrl,
            //         pageUrl: window.location.href,
            //         type: 'website'
            //     });

            //     console.log("Social media preview tags updated for homepage with URL:", defaultPreviewUrl);
            //     return; // Skip full initialization for preview crawlers
            // }

            // Normal initialization for real users
            await fetchAuth(); // Ensure token is fresh on mount
            sendPage("home");
            await fetchBlogsData();

          } catch (error) {
              console.error('Initialization failed:', error);
          }
      };

      initializeData();

      return () => {
          ImageCache.clear();
          setImgSrc([])
      };
  }, []);


  return (
    <Box sx={{
      backgroundColor: '#5C7373',
      minHeight: '100vh',
      pb: { xs: 4, sm: 6 } // Add bottom padding for mobile
      }}>
      <Hero />
      <Container>
        <hr style={{
         height: '12px',
         border: '0',
         width: '100%',
         'box-shadow': 'inset 0 12px 12px -12px rgba(0, 0, 0, 0.5)'}}/>
        <Box flex={1}>
          <Typography
              className="Music" 
              align='center' 
              variant='h3' 
              sx={{
                color: "white",
                fontFamily: "Light-1",
                fontSize: { 
                  xs: '1.75rem',  // Small mobile devices (0-600px)
                  sm: '2.25rem',  // Tablets (600-900px)
                  md: '2.75rem',  // Small laptops (900-1200px)
                  lg: '3rem'      // Desktops (1200px+)
                }, // Responsive font size
                mb: { xs: 2, sm: 4 }
                }}>                  
            <b style={{
              color: "white",
              textShadow: '4px 4px black'
              }}>Music</b>
          </Typography>

          <BackToTopButton />

        </Box>        
          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={{ xs: 3, sm: 4, md: 8 }}  // Increased spacing on mobile
            mt={{ xs: 2, sm: 4, md: 8 }}>
            {/* Songs Section */}
            <Box 
              flex={3} 
              sx={{ 
                padding: {
                  xs: "16px",
                  sm: "24px",
                  md: "18px 100px 100px 100px", },
                position: 'relative'
                }}>
              <Suspense fallback={
                <SpinnerWrapper>
                  <Spinner />
                  <Typography sx={{ 
                    color: 'white', 
                    mt: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>Loading songs...</Typography>
                </SpinnerWrapper>
              }>
                <LazySongs />
              </Suspense>
            </Box>

            {/* Rightbar Section */}
            <Box 
              flex={1} 
              sx={{ 
                position: 'relative',
                width: '100%',  // Full width on all screens
                maxWidth: { xs: '100%', sm: '450px', md: '100%' }, // Limit width on tablet
                mx: 'auto',  // Center on mobile and tablet
                mt: { xs: 2, md: 0 },
                overflow: 'hidden',  // Ensure content stays within borders
              }}>
              <Suspense fallback={
                <SpinnerWrapper>
                  <Spinner />
                  <Typography sx={{ 
                    color: 'white', 
                    mt: 2,
                    fontSize: { xs: '0.875rem', sm: '1rem' }
                    }}>Loading popular content...</Typography>
                </SpinnerWrapper>
              }>
                {loadingStates.popularImages ? (
                  <SpinnerWrapper>
                    <Typography sx={{ 
                      color: 'white', 
                      mb: 2,
                      fontSize: { xs: '0.875rem', sm: '1rem' }
                      }}>
                      Loading images... ({Object.keys(blogImageMap).length} of {blogData?.data?.length})
                    </Typography>
                    <Spinner />
                  </SpinnerWrapper>
                ) : (
                  blogData && <LazyRightbar 
                    blogs_popular={blogData.data} 
                    imgSrc={imgSrc} 
                    blogImageMap={blogImageMap} 
                    isLoading={false} 
                  />
                )}
              </Suspense>
            </Box>
          </Stack>
      </Container>
    </Box>
  );
};

export default Home;